import React, { useState } from 'react';
import { ChevronLeft} from 'lucide-react';

const MultiStepDatePicker = ({ 
  value, 
  onChange, 
  placeholder = "MM/DD/YYYY",
  className = "",
  maxDate = new Date(),
  minDate = new Date(1930, 0, 1)
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState('decade'); // decade, year, month, date
  const [selectedDecade, setSelectedDecade] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);

  const generateDecades = () => {
    const currentYear = new Date().getFullYear();
    const decades = [];
    for (let start = 1930; start <= currentYear; start += 10) {
      const end = Math.min(start + 9, currentYear);
      decades.push({ start, end, label: `${start}-${end}` });
    }
    return decades;
  };

  const generateYears = (decade) => {
    const years = [];
    const currentYear = new Date().getFullYear();
    for (let year = decade.start; year <= Math.min(decade.end, currentYear); year++) {
      years.push(year);
    }
    return years;
  };

  const generateMonths = (year) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const currentYear = maxDate.getFullYear();
    const currentMonth = maxDate.getMonth();

    return months
      .map((month, index) => ({ name: month, value: index }))
      .filter((month) => {
        // If selected year is current year, only show months up to current month
        if (year === currentYear) {
          return month.value <= currentMonth;
        }
        return true;
      });
  };

  const generateDates = (year, month) => {
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const dates = [];
    for (let date = 1; date <= daysInMonth; date++) {
      const dateObj = new Date(year, month, date);
      if (dateObj <= maxDate && dateObj >= minDate) {
        dates.push(date);
      }
    }
    return dates;
  };

  const formatDate = (date) => {
    if (!date) return '';
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const handleDecadeSelect = (decade) => {
    setSelectedDecade(decade);
    setCurrentStep('year');
  };

  const handleYearSelect = (year) => {
    setSelectedYear(year);
    setCurrentStep('month');
  };

  const handleMonthSelect = (month) => {
    setSelectedMonth(month);
    setCurrentStep('date');
  };

  const handleDateSelect = (date) => {
    const selectedDate = new Date(selectedYear, selectedMonth, date);
    onChange(selectedDate);
    setIsOpen(false);
    resetSteps();
  };

  const resetSteps = () => {
    setCurrentStep('decade');
    setSelectedDecade(null);
    setSelectedYear(null);
    setSelectedMonth(null);
  };

  const goBack = () => {
    switch (currentStep) {
      case 'year':
        setCurrentStep('decade');
        setSelectedDecade(null);
        break;
      case 'month':
        setCurrentStep('year');
        setSelectedYear(null);
        break;
      case 'date':
        setCurrentStep('month');
        setSelectedMonth(null);
        break;
      default:
        break;
    }
  };

  const decades = generateDecades();

  return (
    <div className={`multidate-container ${className}`}>
      <div 
        className="calendar-container"
        onClick={() => setIsOpen(!isOpen)}
      >
     
        <input
          type="text"
          value={value ? formatDate(value) : ''}
          placeholder={placeholder}
          readOnly
          className="calendar-input"
        />
      </div>

      {isOpen && (
        <div className="dropdown">
          <div className="dropdown-inner">
            
            <div className="dropdown-header">
              {currentStep !== 'decade' && (
                <button onClick={goBack} className="back-btn">
                  <ChevronLeft className="icon-left" />
                  Back
                </button>
              )}
              <div className="header-title">
                {currentStep === 'decade' && 'Select Decade'}
                {currentStep === 'year' && `Select Year (${selectedDecade?.label})`}
                {currentStep === 'month' && `Select Month (${selectedYear})`}
                {currentStep === 'date' && `Select Date (${generateMonths(selectedYear).find(m => m.value === selectedMonth)?.name} ${selectedYear})`}
              </div>
              <button
                onClick={() => { setIsOpen(false); resetSteps(); }}
                className="close-btn"
              >
                ✕
              </button>
            </div>

            <div className="dropdown-body">
              {currentStep === 'decade' && (
                <div className="grid grid-2">
                  {decades.map((decade) => (
                    <button 
                      key={decade.label} 
                      onClick={() => handleDecadeSelect(decade)}
                      className="grid-btn"
                    >
                      {decade.label}
                    </button>
                  ))}
                </div>
              )}

              {currentStep === 'year' && selectedDecade && (
                <div className="grid grid-3">
                  {generateYears(selectedDecade).map((year) => (
                    <button 
                      key={year} 
                      onClick={() => handleYearSelect(year)}
                      className="grid-btn"
                    >
                      {year}
                    </button>
                  ))}
                </div>
              )}

              {currentStep === 'month' && selectedYear && (
                <div className="grid grid-3">
                  {generateMonths(selectedYear).map((month) => (
                    <button
                      key={month.value}
                      onClick={() => handleMonthSelect(month.value)}
                      className="grid-btn"
                    >
                      {month.name}
                    </button>
                  ))}
                </div>
              )}

              {currentStep === 'date' && selectedYear && selectedMonth !== null && (
                <div className="grid grid-7">
                  {['Su','Mo','Tu','We','Th','Fr','Sa'].map(day => (
                    <div key={day} className="day-header">{day}</div>
                  ))}

                  {Array.from({ length: new Date(selectedYear, selectedMonth, 1).getDay() }, (_, i) => (
                    <div key={`empty-${i}`} />
                  ))}

                  {generateDates(selectedYear, selectedMonth).map((date) => (
                    <button 
                      key={date} 
                      onClick={() => handleDateSelect(date)}
                      className="grid-btn"
                    >
                      {date}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiStepDatePicker;